import React, { useRef, useEffect } from 'react';
import { func, object } from 'prop-types';
import { getContext } from '@fiverr-private/fiverr_context';
import { translate } from '@fiverr-private/i18n-react';
import { Button } from '@fiverr-private/button';
import { Grid, Container, Stack } from '@fiverr-private/layout_components';
import { Heading, Typography } from '@fiverr-private/typography';

import { Box } from '@fiverr-private/theme';
import { trackSignUp } from '../../shared/gtmTracking';
import { useFormInput, FormInput } from '../../shared/FormInput';
import { useFormSelect, FormSelect } from '../../shared/FormSelect';
import { useFormRadioGroup, FormRadioGroup } from '../../shared/FormRadioGroup';
import { logger } from '../../../lib/logger';
import { SUPPORTED_LOCALES, SUPPORTED_COUNTRIES } from '../../const';
import { PERSONAL_FIELDS } from '../constants';
import { useEmailField } from './fields/email';
import { useUsernameField } from './fields/username';
import { Username } from './components/Username';
import { Email } from './components/Email';

import { personalInfoClass } from './styles.ve.css';

const TELEGRAM_MAX_LENGTH = 64;

const validInput = (val) => !val.match(/[^A-Za-z0-9_ \-@.()]+/);

const fiverrSellerProfileValidation = (url) => {
    if (!url || url.trim().length === 0) {
        return translate('affiliates_signup_perseus.errors.url_not_valid');
    }

    const trimmedUrl = url.trim();

    const fiverrUrlPattern =
        /^(?:http(s)?:\/\/)?(?:www\.)?(?:\w+\.)?fiverr\.com(?:\/(?:agencies\/)?[a-zA-Z][\w_]{5,14})?\/?$/;

    if (!fiverrUrlPattern.test(trimmedUrl)) {
        return translate('affiliates_signup_perseus.errors.url_not_valid');
    }

    return '';
};
const validNotEmptyInput = (val) => !!val && validInput(val);
const notEmpty = (value) => !!value;
const validCountry = (val) => val && val.match(/^[A-Z]{2}$/);
const validLanguage = (val) => val && val.match(/^[a-z]{2}(?:-[A-Z]{2})?$/);
const passwordValidation = (val) => val.match(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/);
const maxLength = (maxLength) => (value) => value.length <= maxLength;
const telegramValidation = (value) => maxLength(TELEGRAM_MAX_LENGTH)(value.trim());
const fiverrSellerUsernameValidation = (val) => /^[A-Za-z][A-Za-z0-9_]{5,14}$/.test(val);
// const optionalValidation = (handler) => (val) => val ? handler(val) : true;
const when =
    (condition, defaultValue) =>
    (validator) =>
    (...args) =>
        condition() ? validator(...args) : defaultValue;

const validationWithReason =
    (validation) =>
    (...args) =>
        [validation(...args), ''];

const socialUrlRules = [
    {
        domain: 'facebook.com',
        fullNameTemplate: '/facebook\\.com\\/.+/',
    },
    {
        domain: 'instagram.com',
        fullNameTemplate: '/instagram\\.com\\/.+/',
    },
    {
        domain: 'linkedin.com',
        fullNameTemplate: '/linkedin\\.com\\/.+/',
    },
    {
        domain: 'quora.com',
        fullNameTemplate: '/quora\\.com\\/.+/',
    },
    {
        domain: 'reddit.com',
        fullNameTemplate: '/reddit\\.com\\/.+/',
    },
    {
        domain: 'pinterest.com',
        fullNameTemplate: '/pinterest\\.com\\/.+/',
    },
    {
        domain: 'tiktok.com',
        fullNameTemplate: '/tiktok\\.com\\/.+/',
    },
    {
        domain: 'youtube.com',
        fullNameTemplate: '/youtube\\.com\\/.+/',
    },
];

const verifySocialUrl = (url) => {
    if (!url || url.trim().length === 0) {
        return translate('affiliates_signup_perseus.errors.url_not_valid');
    }

    const trimmedUrl = url.trim();

    try {
        const urlPattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w._~:/?#[\]@!$&'()*+,;=-]+$/;

        if (!urlPattern.test(trimmedUrl)) {
            return translate('affiliates_signup_perseus.errors.url_not_valid');
        }

        const hasProtocol = /^https?:\/\//i.test(trimmedUrl);
        const urlForParsing = hasProtocol ? trimmedUrl : `https://${trimmedUrl}`;

        let parsedUrl;
        try {
            parsedUrl = new URL(urlForParsing);
        } catch (error) {
            return translate('affiliates_signup_perseus.errors.url_not_valid');
        }

        const hostname = parsedUrl.hostname.replace(/^www\./, '');

        for (const rule of socialUrlRules) {
            if (hostname === rule.domain) {
                const regexPattern = rule.fullNameTemplate.slice(1, rule.fullNameTemplate.lastIndexOf('/'));
                const regex = new RegExp(regexPattern);

                if (!regex.test(trimmedUrl) && !regex.test(urlForParsing)) {
                    return translate('affiliates_signup_perseus.errors.complete_link_specific_account');
                }
            }
        }
    } catch (error) {
        return translate('affiliates_signup_perseus.errors.url_not_valid');
    }

    return '';
};

const socialUrlValidation = (val) => verifySocialUrl(val) === '';

// Simple validation function that returns [boolean, string] format
const socialUrlValidationWithReason = (val) => {
    const errorMessage = verifySocialUrl(val);
    return [errorMessage === '', errorMessage];
};

const PersonalInfo = ({ onSubmit, formData, onStart }) => {
    const formStarted = useRef(false);

    const companyNameValidation = (val) => accountType.value !== 'company' || validNotEmptyInput(val);
    const fiverrSellerValidation = (val) =>
        accountType.value !== 'fiverr_seller' || (validNotEmptyInput(val) && fiverrSellerUsernameValidation(val));

    const isSourceOfTrafficActiveYes = () => hasSourceOfTraffic.value === 'yes';
    const isSourceOfTrafficActiveNo = () => hasSourceOfTraffic.value === 'no';
    const whenSourceOfTrafficActiveYes = when(isSourceOfTrafficActiveYes, true);
    const whenSourceOfTrafficActiveNo = when(isSourceOfTrafficActiveNo, true);

    const socialMediaUrlValidation = whenSourceOfTrafficActiveNo(socialUrlValidation);

    const confirmPasswordValidation = (value) => notEmpty(value) && password.value === value;

    const getFormFieldsLength = () => Object.keys(getPersonalInfoFieldsValues()).length;

    const onFieldInteracted = (name, ref) => {
        if (!formStarted.current) {
            onStart({ formFields: getFormFieldsLength() });
            formStarted.current = true;
        }
        const { userId } = getContext();

        // @TODO
        // Refactor error attribute - we should avoid tag binding
        // Ref should be removed.
        const error = (ref && ref.attributes['data-error']) || null;

        trackSignUp({
            action: 'Form Field',
            formErrors: getNumberFormFieldErrors(),
            formFieldName: name,
            formMessagesText: error && error.value,
            formMessagesType: error && 'error',
            formFields: getFormFieldsLength(),
            formType: 'Sign-up',
            userId,
        });
    };

    const onSelectInteracted = (name) => () => onFieldInteracted(name);

    const reportSubmit = () => {
        const { userId } = getContext();

        trackSignUp({
            action: 'Form Next',
            formErrors: getNumberFormFieldErrors(),
            formFields: getFormFieldsLength(),
            formType: 'Sign-up',
            userId,
        });
    };

    const accountType = useFormRadioGroup({
        name: PERSONAL_FIELDS.ACCOUNT_TYPE,
        initialValue: formData.accountType || '',
        required: true,
        onFirstTouch: onFieldInteracted,
    });

    const companyName = useFormInput({
        name: PERSONAL_FIELDS.COMPANY_NAME,
        initialValue: formData.companyName,
        onFieldInteracted,
        validationFunc: validationWithReason(companyNameValidation),
    });

    const fiverrSeller = useFormInput({
        name: PERSONAL_FIELDS.FIVERR_SELLER_USERNAME,
        initialValue: formData.fiverrSeller || '',
        onFieldInteracted,
        validationFunc: validationWithReason(fiverrSellerValidation),
    });

    const firstName = useFormInput({
        name: PERSONAL_FIELDS.FIRST_NAME,
        initialValue: formData.firstName,
        onFieldInteracted,
        validationFunc: validationWithReason(validNotEmptyInput),
    });
    const lastName = useFormInput({
        name: PERSONAL_FIELDS.LAST_NAME,
        initialValue: formData.lastName,
        onFieldInteracted,
        validationFunc: validationWithReason(validNotEmptyInput),
    });
    const email = useEmailField(formData.email, onFieldInteracted);
    const username = useUsernameField(formData.username, onFieldInteracted);
    const password = useFormInput({
        name: PERSONAL_FIELDS.PASSWORD,
        initialValue: formData.password,
        onFieldInteracted,
        validationFunc: validationWithReason(passwordValidation),
    });
    const confirmPassword = useFormInput({
        name: PERSONAL_FIELDS.CONFIRM_PASSWORD,
        initialValue: formData.confirmPassword,
        onFieldInteracted,
        validationFunc: validationWithReason(confirmPasswordValidation),
    });
    const country = useFormSelect({
        initialValue: formData.country,
        onFieldInteracted: onSelectInteracted(PERSONAL_FIELDS.COUNTRY),
        validationFunc: validCountry,
    });
    const language = useFormSelect({
        initialValue: formData.language || 'en-US',
        onFieldInteracted: onSelectInteracted(PERSONAL_FIELDS.LANGUAGE),
        validationFunc: validLanguage,
    });
    const telegram = useFormInput({
        name: PERSONAL_FIELDS.TELEGRAM,
        initialValue: formData.telegram,
        onFieldInteracted,
        validationFunc: validationWithReason(telegramValidation),
    });
    const hasSourceOfTraffic = useFormSelect({
        initialValue: formData[PERSONAL_FIELDS.HAS_SOURCE_OF_TRAFFIC],
        onFieldInteracted: onSelectInteracted(PERSONAL_FIELDS.HAS_SOURCE_OF_TRAFFIC),
        validationFunc: validNotEmptyInput,
    });
    const mainSourceOfTrafficValidation = whenSourceOfTrafficActiveYes((val) =>
        accountType.value === 'fiverr_seller' ? fiverrSellerProfileValidation(val) === '' : socialUrlValidation(val)
    );

    const mainSourceOfTrafficValidationWithReason = (val) => {
        if (hasSourceOfTraffic.value !== 'yes') return [true, ''];
        if (accountType.value === 'fiverr_seller') {
            const error = fiverrSellerProfileValidation(val);
            return [error === '', error];
        }
        return socialUrlValidationWithReason(val);
    };

    const mainSourceOfTraffic = useFormInput({
        name: PERSONAL_FIELDS.MAIN_SOURCE_OF_TRAFFIC,
        initialValue: formData.mainSourceOfTraffic,
        onFieldInteracted,
        validationFunc: mainSourceOfTrafficValidationWithReason,
    });
    const socialMediaUrl = useFormInput({
        name: PERSONAL_FIELDS.SOCIAL_MEDIA_URL,
        initialValue: formData.socialMediaUrl,
        onFieldInteracted,
        validationFunc: (val) => (hasSourceOfTraffic.value === 'no' ? socialUrlValidationWithReason(val) : [true, '']),
    });

    const getPersonalInfoFieldsValues = () => ({
        [PERSONAL_FIELDS.ACCOUNT_TYPE]: accountType.value,
        [PERSONAL_FIELDS.COMPANY_NAME]: accountType.value === 'company' ? companyName.value : '',
        [PERSONAL_FIELDS.FIVERR_SELLER_USERNAME]: accountType.value === 'fiverr_seller' ? fiverrSeller.value : '',
        [PERSONAL_FIELDS.FIRST_NAME]: firstName.value,
        [PERSONAL_FIELDS.LAST_NAME]: lastName.value,
        [PERSONAL_FIELDS.EMAIL]: email.value,
        [PERSONAL_FIELDS.USERNAME]: username.value,
        [PERSONAL_FIELDS.PASSWORD]: password.value,
        [PERSONAL_FIELDS.COUNTRY]: country.value,
        [PERSONAL_FIELDS.LANGUAGE]: language.value,
        [PERSONAL_FIELDS.TELEGRAM]: telegram.value.trim(),
        [PERSONAL_FIELDS.HAS_SOURCE_OF_TRAFFIC]: hasSourceOfTraffic.value,
        [PERSONAL_FIELDS.MAIN_SOURCE_OF_TRAFFIC]: hasSourceOfTraffic.value === 'yes' ? mainSourceOfTraffic.value : '',
        [PERSONAL_FIELDS.SOCIAL_MEDIA_URL]: hasSourceOfTraffic.value === 'no' ? socialMediaUrl.value : '',
    });

    const submit = () => {
        const personalInfo = getPersonalInfoFieldsValues();

        reportSubmit();
        onSubmit(personalInfo);
    };

    const handleSubmit = () => {
        if (isFormValid()) {
            submit();
        }
    };

    const getFormFields = () => [
        accountType,
        companyName,
        fiverrSeller,
        firstName,
        lastName,
        email,
        username,
        password,
        confirmPassword,
        country,
        language,
        hasSourceOfTraffic,
        mainSourceOfTraffic,
        socialMediaUrl,
        telegram,
    ];

    const getNumberFormFieldErrors = () => getFormFields().filter((field) => !field.valid && field.touched).length;

    const isFormValid = () => {
        // Revalidate dependent fields
        companyName.valid = companyNameValidation(companyName.value);
        fiverrSeller.valid = fiverrSellerValidation(fiverrSeller.value);
        mainSourceOfTraffic.valid = mainSourceOfTrafficValidation(mainSourceOfTraffic.value);
        socialMediaUrl.valid = socialMediaUrlValidation(socialMediaUrl.value);
        confirmPassword.valid = confirmPasswordValidation(confirmPassword.value);

        return getFormFields().every((field) => field.valid);
    };

    const accountTypeOptions = () => [
        {
            text: translate('affiliates_signup_perseus.account_types.private'),
            value: 'private',
        },
        {
            text: translate('affiliates_signup_perseus.account_types.company'),
            value: 'company',
        },
        {
            text: translate('affiliates_signup_perseus.account_types.fiverr_seller'),
            value: 'fiverr_seller',
        },
    ];

    const sourceOfTrafficOptions = () => [
        {
            text: translate('affiliates_signup_perseus.source_of_traffics.yes'),
            value: 'yes',
        },
        {
            text: translate('affiliates_signup_perseus.source_of_traffics.no'),
            value: 'no',
        },
    ];

    const supportedCountryOptions = () =>
        SUPPORTED_COUNTRIES.map((countryCode) => ({
            value: countryCode,
            text: translate(`countries.${countryCode}`),
        }));

    const localeOptions = () =>
        SUPPORTED_LOCALES.map((locale) => ({
            value: locale,
            text: translate(`languages.${locale}`),
        }));

    const getMainSourceTrafficPlaceholder = () =>
        accountType.value === 'fiverr_seller'
            ? translate('affiliates_signup_perseus.personal_info.fiverr_seller_profile_url')
            : translate('affiliates_signup_perseus.personal_info.main_source_of_traffic');

    const getMainSourceTrafficHint = () =>
        accountType.value === 'fiverr_seller'
            ? translate('affiliates_signup_perseus.personal_info.fiverr_seller_profile_example')
            : translate('affiliates_signup_perseus.personal_info.social_media_url_example');

    useEffect(() => {
        logger.info('Showing personal info');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <form className={personalInfoClass} onSubmit={handleSubmit}>
            <Stack paddingBottom="2" alignItems="flexEnd" gap="1">
                <Heading as="h2" paddingRight="1">
                    {translate('affiliates_signup_perseus.personal_info.title')}
                </Heading>
                <Typography size="b_sm">{translate('affiliates_signup_perseus.personal_info.subtitle')}</Typography>
            </Stack>

            <Container paddingBottom="8">
                <Grid gap="5" templateColumns="50%_50%">
                    <FormInput
                        {...firstName}
                        autoComplete="given-name"
                        placeholder={translate('affiliates_signup_perseus.personal_info.first_name')}
                        isRequired
                        errorMessage={translate('affiliates_signup_perseus.errors.allowed_characters')}
                    />
                    <FormInput
                        {...lastName}
                        autoComplete="family-name"
                        placeholder={translate('affiliates_signup_perseus.personal_info.last_name')}
                        isRequired
                        errorMessage={translate('affiliates_signup_perseus.errors.allowed_characters')}
                    />
                </Grid>
                <Email {...email} />
                <Username {...username} />
                <Grid gap="5" templateColumns="50%_50%">
                    <FormInput
                        type="password"
                        {...password}
                        autoComplete="new-password"
                        placeholder={translate('affiliates_signup_perseus.personal_info.password')}
                        isRequired
                        errorMessage={translate('affiliates_signup_perseus.errors.password')}
                    />
                    <FormInput
                        type="password"
                        {...confirmPassword}
                        autoComplete="new-password"
                        placeholder={translate('affiliates_signup_perseus.personal_info.password_confirm')}
                        isRequired
                        errorMessage={translate('affiliates_signup_perseus.errors.password_match')}
                    />
                </Grid>
                <Stack marginTop="3">
                    <Heading as="h3" size="b_md">
                        {translate('affiliates_signup_perseus.personal_info.account_type')}:
                    </Heading>
                    <Typography color="red_700"> *</Typography>
                </Stack>
                <Box marginTop="2" marginBottom="3">
                    <FormRadioGroup
                        options={accountTypeOptions()}
                        name={accountType.name}
                        value={accountType.value}
                        onChange={accountType.onChange}
                        direction="column"
                    />
                </Box>
                {accountType.value === 'company' && (
                    <FormInput
                        {...companyName}
                        placeholder={translate('affiliates_signup_perseus.personal_info.company_name')}
                        isRequired
                    />
                )}
                {accountType.value === 'fiverr_seller' && (
                    <FormInput
                        {...fiverrSeller}
                        placeholder={translate('affiliates_signup_perseus.personal_info.fiverr_username')}
                        isRequired
                        errorMessage={translate('affiliates_signup_perseus.errors.not_valid_fiverr_seller_username')}
                    />
                )}
                <FormSelect
                    {...hasSourceOfTraffic}
                    options={sourceOfTrafficOptions()}
                    placeholder={translate('affiliates_signup_perseus.personal_info.source_of_traffic')}
                    isRequired
                />
                {hasSourceOfTraffic.value === 'yes' && (
                    <FormInput
                        {...mainSourceOfTraffic}
                        placeholder={getMainSourceTrafficPlaceholder()}
                        hint={getMainSourceTrafficHint()}
                        isRequired
                    />
                )}
                {hasSourceOfTraffic.value === 'no' && (
                    <FormInput
                        {...socialMediaUrl}
                        placeholder={translate('affiliates_signup_perseus.personal_info.social_media_url')}
                        hint={translate('affiliates_signup_perseus.personal_info.social_media_url_example')}
                        isRequired
                    />
                )}
                <Grid gap="5" templateColumns="50%_50%">
                    <FormSelect
                        {...country}
                        options={supportedCountryOptions()}
                        placeholder={translate('affiliates_signup_perseus.personal_info.country')}
                        isRequired
                    />
                    <FormSelect
                        {...language}
                        options={localeOptions()}
                        placeholder={translate('affiliates_signup_perseus.personal_info.language')}
                        isRequired
                    />
                </Grid>
                <div>
                    <FormInput
                        {...telegram}
                        placeholder={translate('affiliates_signup_perseus.personal_info.telegram')}
                        errorMessage={translate('affiliates_signup_perseus.errors.telegram', {
                            params: { maxLength: TELEGRAM_MAX_LENGTH },
                        })}
                    />
                </div>
            </Container>

            <Button size="lg" onClick={handleSubmit} disabled={!isFormValid()} colorScheme="green">
                {translate('affiliates_signup_perseus.next')}
            </Button>
        </form>
    );
};

PersonalInfo.propTypes = {
    onSubmit: func,
    formData: object,
    onStart: func,
};

export default PersonalInfo;
